import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShieldAlert, RefreshCw, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Panel read-only para auditar la calidad de datos desde el admin.
 * Consulta GET /api/admin/audit/data-quality y muestra:
 *   - Usuarios duplicados por casing de email (bug histórico de "0 evaluaciones")
 *   - Distribución de exercise_recommendation.estado
 *   - Distribución cruda de activity_level
 *
 * No modifica nada. Solo lectura.
 */
export default function DataQualityAuditSection({ adminSecret, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const fetchAudit = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/audit/data-quality`, {
        headers: {
          'X-Admin-Secret': adminSecret,
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const msg = `HTTP ${res.status}`;
        throw new Error(msg);
      }
      const payload = await res.json();
      setData(payload);
      toast.success('Auditoría completada');
    } catch (e) {
      setErr(e.message || 'Error al ejecutar la auditoría');
      toast.error('No se pudo ejecutar la auditoría');
    } finally {
      setLoading(false);
    }
  };

  const dupCount = data?.duplicate_emails_by_casing?.length || 0;

  return (
    <section
      data-testid="data-quality-audit-section"
      className="rounded-2xl border border-white/10 bg-white/5 p-6"
    >
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert className="h-4 w-4 text-amber-400" />
            <h2 className="text-xs uppercase tracking-wider text-amber-400 font-semibold">
              Auditoría de datos
            </h2>
          </div>
          <p className="text-slate-300 text-sm">
            Read-only. Detecta duplicados por casing de email y muestra la distribución
            de estado y actividad en la base.
          </p>
        </div>
        <Button
          onClick={fetchAudit}
          disabled={loading}
          data-testid="run-audit-btn"
          className="bg-amber-500/20 border border-amber-500/40 text-amber-100 hover:bg-amber-500/30"
        >
          {loading ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Ejecutar auditoría
        </Button>
      </div>

      {err && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          {err}
        </div>
      )}

      {!data && !loading && !err && (
        <p className="text-slate-500 text-sm italic">
          Presioná «Ejecutar auditoría» para consultar el estado de la base.
        </p>
      )}

      {data && (
        <div className="space-y-6">
          {/* --- Duplicados por casing --- */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              {dupCount > 0 ? (
                <AlertTriangle className="h-4 w-4 text-red-400" />
              ) : null}
              <h3 className="text-slate-200 text-sm font-semibold">
                Duplicados por casing de email
                <span
                  className={`ml-2 text-xs font-mono ${
                    dupCount > 0 ? 'text-red-400' : 'text-emerald-400'
                  }`}
                  data-testid="dup-count"
                >
                  ({dupCount})
                </span>
              </h3>
            </div>
            {dupCount === 0 ? (
              <p className="text-emerald-400 text-sm">Sin duplicados. Todo limpio.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-red-500/20">
                <table className="min-w-full text-xs">
                  <thead className="bg-red-500/10 text-red-200">
                    <tr>
                      <th className="text-left px-3 py-2">Email (lower)</th>
                      <th className="text-left px-3 py-2">User ID</th>
                      <th className="text-left px-3 py-2">Email real (case)</th>
                      <th className="text-left px-3 py-2">Nombre</th>
                      <th className="text-left px-3 py-2">Cuenta</th>
                      <th className="text-right px-3 py-2">Entrevistas</th>
                      <th className="text-left px-3 py-2">Alta</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {data.duplicate_emails_by_casing.map((group) =>
                      group.users.map((u, idx) => (
                        <tr
                          key={`${group.email_lower}-${u.user_id}`}
                          className="hover:bg-white/5"
                          data-testid={`dup-row-${u.user_id}`}
                        >
                          {idx === 0 && (
                            <td
                              rowSpan={group.users.length}
                              className="px-3 py-2 text-slate-300 font-mono align-top border-r border-white/5"
                            >
                              {group.email_lower}
                            </td>
                          )}
                          <td className="px-3 py-2 text-slate-500 font-mono">{u.user_id}</td>
                          <td className="px-3 py-2 text-slate-200 font-mono">{u.email}</td>
                          <td className="px-3 py-2 text-slate-300">{u.name || '—'}</td>
                          <td className="px-3 py-2 text-slate-400">{u.account_type || '—'}</td>
                          <td className="px-3 py-2 text-right text-slate-200">
                            {u.evaluations_count}
                          </td>
                          <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                            {u.created_at
                              ? new Date(u.created_at).toLocaleDateString('es-AR')
                              : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* --- Distribución de estado --- */}
          <div>
            <h3 className="text-slate-200 text-sm font-semibold mb-2">
              Distribución de escenarios (
              <code className="text-slate-500 font-mono text-xs">
                exercise_recommendation.estado
              </code>
              )
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {data.estado_distribution.map((row) => (
                <div
                  key={row.estado || 'null'}
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 flex justify-between items-center"
                  data-testid={`estado-${row.estado || 'null'}`}
                >
                  <span className="text-slate-300 font-mono text-xs">
                    {row.estado || '(null)'}
                  </span>
                  <span className="text-white font-semibold">{row.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* --- Distribución de activity_level (raw) --- */}
          <div>
            <h3 className="text-slate-200 text-sm font-semibold mb-2">
              Distribución de nivel de actividad{' '}
              <span className="text-slate-500 font-normal text-xs">
                (crudo — antes de normalizar)
              </span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {data.activity_level_distribution.map((row) => (
                <div
                  key={row.activity_level || 'null'}
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 flex justify-between items-center"
                  data-testid={`activity-${row.activity_level || 'null'}`}
                >
                  <span className="text-slate-300 font-mono text-xs">
                    {row.activity_level || '(null)'}
                  </span>
                  <span className="text-white font-semibold">{row.count}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-slate-600 text-xs text-right">
            Generado: {new Date(data.generated_at).toLocaleString('es-AR')}
          </p>
        </div>
      )}
    </section>
  );
}
